import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { services } from '@/mocks/services';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

// Map each service to the most relevant form
const SERVICE_FORM_MAP: Record<string, { path: string; label: string; icon: string; badge: string; badgeColor: string }> = {
  'ewaste-pickup': {
    path: '/pickup-request',
    label: 'Schedule a Pickup',
    icon: 'ri-truck-line',
    badge: 'Free Pickup Available',
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  'electronics-recycling': {
    path: '/dropoff-form',
    label: 'Schedule a Drop-Off',
    icon: 'ri-map-pin-2-line',
    badge: 'Drop-Off Service',
    badgeColor: 'bg-sky-100 text-sky-700',
  },
  'hard-drive-shredding': {
    path: '/contact',
    label: 'Request This Service',
    icon: 'ri-mail-send-line',
    badge: 'Contact for Quote',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  'data-destruction': {
    path: '/contact',
    label: 'Request This Service',
    icon: 'ri-mail-send-line',
    badge: 'Contact for Quote',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  'cell-phone-destruction': {
    path: '/contact',
    label: 'Request This Service',
    icon: 'ri-mail-send-line',
    badge: 'Contact for Quote',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  'tape-product-destruction': {
    path: '/contact',
    label: 'Request This Service',
    icon: 'ri-mail-send-line',
    badge: 'Contact for Quote',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
};

const DEFAULT_FORM = {
  path: '/contact',
  label: 'Request This Service',
  icon: 'ri-mail-send-line',
  badge: 'Contact for Quote',
  badgeColor: 'bg-amber-100 text-amber-700',
};

const servicesFaqs = [
  {
    q: 'What types of electronics do you accept for recycling?',
    a: 'We accept virtually all electronic devices — TVs (CRT, flat screen, plasma, OLED), computers (desktops, laptops, Macs, servers), monitors, printers, batteries (car, solar, lithium-ion, EV), hard drives, cell phones, networking equipment, cables, and small appliances. If it plugs in or has a battery, we can recycle it responsibly.',
  },
  {
    q: 'Is electronics recycling free at Greenly?',
    a: 'Yes! Residential drop-off is always free at both our Los Angeles locations: 2542 W. 3rd Street (90057) and 4902 W. Slauson Ave (90056). Free business pickup is available for qualifying loads throughout LA County — typically 10+ devices or significant volume. Call (213) 600-1000 to confirm.',
  },
  {
    q: 'How do I schedule a free electronics pickup?',
    a: 'You can schedule a free pickup by calling (213) 600-1000 or filling out our online pickup request form. We will confirm your location, the volume of electronics, and a convenient pickup time. Free pickup is available for qualifying business loads across all of Los Angeles County.',
  },
  {
    q: 'Do you wipe data before recycling computers and hard drives?',
    a: 'Yes. Every computer we recycle gets free data wiping using DoD 5220.22-M multi-pass overwrite standards. For maximum security, we also offer physical hard drive shredding with an official Certificate of Destruction — HIPAA compliant and suitable for healthcare, financial, and legal industries.',
  },
  {
    q: 'What areas do you serve for electronics recycling?',
    a: 'We serve all of Los Angeles County — Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, Downtown LA, Koreatown, the San Fernando Valley, Silver Lake, Torrance, Redondo Beach, and everywhere in between. Free business pickup covers the entire county.',
  },
  {
    q: 'Do you provide a certificate of recycling?',
    a: 'Yes. We provide official Certificates of Recycling and Certificates of Destruction on request. These documents include serial numbers where applicable and are essential for audit compliance, sustainability reporting, HIPAA requirements, and peace of mind.',
  },
];

const ServicesJsonLd = () => (
  <>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/services#webpage`,
      "url": `${SITE_URL}/services`,
      "name": "Electronics Recycling Services Los Angeles | Greenly",
      "description": "Certified e-waste recycling, data destruction, and responsible disposal for businesses and individuals in Los Angeles. TV recycling, computer recycling, battery recycling, hard drive shredding.",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#business` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": `${SITE_URL}/services` }
        ]
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "TV Recycling Los Angeles", "url": `${SITE_URL}/tv-recycling` },
          { "@type": "ListItem", "position": 2, "name": "Computer Recycling Los Angeles", "url": `${SITE_URL}/computer-recycling` },
          { "@type": "ListItem", "position": 3, "name": "Battery Recycling Los Angeles", "url": `${SITE_URL}/battery-recycling` },
          { "@type": "ListItem", "position": 4, "name": "Hard Drive Destruction Los Angeles", "url": `${SITE_URL}/hard-drive-destruction` }
        ]
      }
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": servicesFaqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    })}</script>
  </>
);

const ServicesPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <title>Electronics Recycling Services Los Angeles | TV, Computer, Battery &amp; Data Destruction | Greenly</title>
      <meta name="description" content="Certified electronics recycling services in Los Angeles — TV, computer &amp; battery recycling, hard drive shredding &amp; data destruction. Free business pickup. CalRecycle CEWD-127531." />
      <meta name="keywords" content="electronics recycling services Los Angeles, e-waste recycling LA, data destruction Los Angeles, hard drive shredding LA" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/services`} />
      <ServicesJsonLd />
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: 'Services' }]} />
            <h1 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-3">Our Services</h1>
            <p className="text-green-200 max-w-xl text-base leading-relaxed">
              Certified e-waste recycling, data destruction, and responsible disposal for businesses and individuals in Los Angeles.
            </p>
          </div>
        </section>

        {/* Quick-action legend */}
        <section className="bg-[#F4FAF7] border-b border-green-100 py-5 px-4 md:px-6">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4 text-sm">
            <span className="text-gray-500 font-medium mr-1">How to get started:</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">
              <i className="ri-truck-line text-base"></i> Free Pickup Available
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-100 text-sky-700 font-medium">
              <i className="ri-map-pin-2-line text-base"></i> Drop-Off Service
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
              <i className="ri-mail-send-line text-base"></i> Contact for Quote
            </span>
          </div>
        </section>

        {/* Services Detail */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-20">
            {services.map((service, index) => {
              const formInfo = SERVICE_FORM_MAP[service.id] ?? DEFAULT_FORM;
              return (
                <article
                  key={service.id}
                  id={service.id}
                  className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
                    index % 2 !== 0 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden shrink-0">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 md:h-80 object-cover object-top"
                      loading="lazy"
                      width={700}
                      height={480}
                    />
                  </div>
                  <div className="w-full lg:w-1/2">
                    {/* Service type badge */}
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full mb-4 ${formInfo.badgeColor}`}>
                      <i className={`${formInfo.icon} text-sm`}></i>
                      {formInfo.badge}
                    </span>

                    <div className="w-12 h-12 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-4">
                      <i className={`${service.icon} text-[#1B6B3A] text-xl`}></i>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{service.title}</h2>
                    <p className="text-gray-500 leading-relaxed mb-6">{service.fullDesc}</p>
                    <ul className="grid grid-cols-2 gap-2 mb-7">
                      {service.items.map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-5 h-5 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                            <i className="ri-check-line text-[#1B6B3A] text-xs"></i>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={formInfo.path}
                      className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={formInfo.icon}></i>
                      {formInfo.label}
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* ── FAQ Section ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Electronics Recycling FAQs — Los Angeles
              </h2>
            </div>
            <div className="space-y-3">
              {servicesFaqs.map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-extrabold text-gray-900 text-sm">{faq.q}</h4>
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                      <i className={`ri-arrow-down-s-line text-[#1B6B3A] transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#F4FAF7] py-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Not Sure Which Service You Need?</h2>
            <p className="text-gray-500 mb-8">
              Contact us and our team will help you find the right recycling solution for your needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/pickup-request"
                className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-truck-line"></i> Schedule a Pickup
              </Link>
              <Link
                to="/dropoff-form"
                className="inline-flex items-center gap-2 border-2 border-[#1B6B3A] text-[#1B6B3A] font-semibold px-7 py-3.5 rounded-full hover:bg-[#E8F5EE] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-2-line"></i> Find a Drop-Off Location
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-gray-300 text-gray-600 font-semibold px-7 py-3.5 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i> Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ServicesPage;